<template>
  <div class="w-full py-6 px-4 space-y-4 fadeIn">

    <!-- Upload trigger button (shown when upload zone is collapsed) -->
    <div v-if="!showUpload" class="flex">
      <Button
        variant="outline"
        size="sm"
        class="flex items-center gap-2 bg-cosmicPurple text-white hover:bg-cosmicPurple/80 hover:text-white"
        @click="showUpload = true"
      >
        <UploadCloud class="w-4 h-4" />
        Upload resume
      </Button>
    </div>

    <!-- Upload section (collapsible) -->
    <div v-if="showUpload">
      <div
class="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 transition-colors"
        :class="isDragging ? 'border-cosmicPurple bg-cosmicPurple/5' : 'hover:border-cosmicPurple/50'"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="onDrop"
      >
        <!-- Drop target -->
        <div class="p-6 text-center cursor-pointer" @click="triggerFileInput">
          <UploadCloud class="mx-auto mb-3 w-8 h-8 text-gray-400" />
          <p class="text-sm font-medium text-gray-700 mb-1">
            Drag &amp; drop your PDF here, or
            <button class="text-cosmicPurple underline underline-offset-2 hover:text-cosmicPurple/80" @click.stop="triggerFileInput">
              browse
            </button>
          </p>
          <p class="text-xs text-gray-500">PDF only · max {{ MAX_RESUMES }} files per account</p>
          <input
            ref="fileInputRef"
            type="file"
            accept="application/pdf"
            class="hidden"
            @change="onFileSelected"
          />
        </div>

        <!-- Filename convention note -->
        <div class="flex gap-2 border-t border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-700 rounded-b-lg">
          <Info class="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <p>
            Name your file using your full name, e.g.
            <code class="bg-blue-100 px-1 rounded font-mono">CV_FirstName_LastName.pdf</code>
            or
            <code class="bg-blue-100 px-1 rounded font-mono">CV_FirstName_LastName_Google.pdf</code>.
            The filename must include the word <strong>CV</strong>.
          </p>
        </div>
      </div>

      <!-- Cancel upload zone -->
      <div class="flex justify-end mt-2">
        <button class="text-xs text-gray-400 hover:text-gray-600 transition-colors" @click="showUpload = false">
          Cancel
        </button>
      </div>
    </div>

    <!-- Upload name + confirm -->
    <div v-if="pendingFile" class="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
      <div class="flex items-center gap-3">
        <FileText class="w-5 h-5 text-cosmicPurple shrink-0" />
        <span class="text-sm font-medium text-gray-800 truncate">{{ pendingFile.name }}</span>
      </div>
      <div class="space-y-1">
        <label class="text-xs font-medium text-gray-600">Label (optional)</label>
        <input
          v-model="pendingName"
          type="text"
          placeholder="e.g. Main resume, Google version…"
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cosmicPurple"
        />
      </div>
      <div v-if="uploadError" class="text-xs text-red-600">{{ uploadError }}</div>
      <div class="flex gap-2 justify-end">
        <Button variant="outline" size="sm" @click="cancelUpload">Cancel</Button>
        <Button size="sm" :disabled="store.uploading" @click="confirmUpload">
          <Loader2 v-if="store.uploading" class="w-3.5 h-3.5 mr-1.5 animate-spin" />
          {{ store.uploading ? 'Uploading…' : 'Upload' }}
        </Button>
      </div>
    </div>

    <!-- Resume list -->
    <div v-if="store.loading" class="flex justify-center py-10">
      <Loader2 class="w-6 h-6 animate-spin text-cosmicPurple" />
    </div>

    <div v-else-if="store.resumeFiles.length === 0" class="text-center py-10 text-sm text-gray-500">
      No resume files yet. Upload your first PDF above.
    </div>

    <!-- Resume list — table-style rows -->
    <div v-else class="space-y-3">
      <template v-for="resume in store.resumeFiles" :key="resume.id">

        <!-- Card -->
        <div
          class="rounded-lg border bg-white overflow-hidden"
          :class="resume.is_default ? 'border-cosmicPurple/40 ring-1 ring-cosmicPurple/20' : 'border-gray-200'"
        >
        <!-- Main row -->
        <div class="flex items-center gap-3 px-4 py-3">
          <FileText class="w-4 h-4 shrink-0 text-gray-400" />

          <!-- Filename + meta + badges -->
          <div class="min-w-0 flex-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            <a
              :href="resume.file || resume.external_url"
              target="_blank"
              rel="noopener noreferrer"
              class="text-sm font-medium text-gray-800 hover:text-cosmicPurple hover:underline underline-offset-2 truncate"
            >
              {{ resume.name || (resume.file ? fileName(resume.file) : 'External resume') }}
            </a>
            <!-- External link indicator (no GCS file) -->
            <span
              v-if="!resume.file && resume.external_url"
              class="inline-flex items-center gap-1 text-xs text-gray-400 whitespace-nowrap"
            >
              <ExternalLink class="w-3 h-3" />
              External link
            </span>
            <span class="text-xs text-gray-400 whitespace-nowrap">
              Uploaded {{ formatDate(resume.created_at) }}
            </span>
            <!-- Status badge -->
            <span
              :class="{
                'bg-green-100 text-green-700 border-green-200': resume.approval_status === 'approved',
                'bg-amber-50 text-amber-700 border-amber-200': resume.approval_status === 'pending',
                'bg-amber-100 text-amber-800 border-amber-300': resume.approval_status === 'revision',
              }"
              class="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border whitespace-nowrap"
            >
              <CheckCircle2 v-if="resume.approval_status === 'approved'" class="w-3 h-3" />
              <TriangleAlert v-else-if="resume.approval_status === 'revision'" class="w-3 h-3" />
              <Clock v-else class="w-3 h-3" />
              {{
                resume.approval_status === 'approved' ? 'Approved'
                : resume.approval_status === 'revision' ? 'Needs revision'
                : 'Pending review'
              }}
            </span>
            <!-- Default badge -->
            <span
              v-if="resume.is_default"
              class="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border bg-cosmicPurple/10 text-cosmicPurple border-cosmicPurple/30 whitespace-nowrap"
            >
              <Star class="w-3 h-3 fill-current" />
              Default
            </span>
          </div>

          <!-- Actions -->
          <div class="flex items-center gap-1 shrink-0">
            <!-- Open link -->
            <a
              :href="resume.file || resume.external_url"
              target="_blank"
              rel="noopener noreferrer"
              title="Open resume"
              class="inline-flex items-center justify-center h-8 w-8 rounded-md border border-gray-200 hover:bg-gray-50 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ExternalLink class="w-3.5 h-3.5" />
            </a>

            <!-- Set / unset default -->
            <template v-if="canManage()">
              <button
                v-if="resume.is_default"
                :disabled="actionLoading === resume.id"
                title="Remove as default"
                class="inline-flex items-center justify-center h-8 w-8 rounded-md border border-cosmicPurple/30 bg-cosmicPurple/10 text-cosmicPurple hover:bg-cosmicPurple/20 transition-colors disabled:opacity-40"
                @click="handleUnsetDefault(resume)"
              >
                <Loader2 v-if="actionLoading === resume.id" class="w-3.5 h-3.5 animate-spin" />
                <Star v-else class="w-3.5 h-3.5 fill-current" />
              </button>
              <button
                v-else-if="resume.approval_status !== 'approved'"
                disabled
                title="Resume needs to be approved to be set as default"
                class="inline-flex items-center justify-center h-8 w-8 rounded-md border border-gray-200 text-gray-300 cursor-not-allowed"
              >
                <StarOff class="w-3.5 h-3.5" />
              </button>
              <button
                v-else
                :disabled="actionLoading === resume.id"
                title="Set as default"
                class="inline-flex items-center justify-center h-8 w-8 rounded-md border border-gray-200 hover:bg-gray-50 text-gray-400 hover:text-cosmicPurple transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                @click="handleSetDefault(resume)"
              >
                <Loader2 v-if="actionLoading === resume.id" class="w-3.5 h-3.5 animate-spin" />
                <Star v-else class="w-3.5 h-3.5" />
              </button>
            </template>

            <!-- Approve (privileged only) -->
            <button
              v-if="isPrivileged && resume.approval_status !== 'approved'"
              :disabled="actionLoading === resume.id"
              title="Approve resume"
              class="inline-flex items-center gap-1 h-8 px-2 rounded-md border border-gray-200 text-xs font-medium text-green-700 hover:bg-green-50 transition-colors disabled:opacity-40"
              @click="handleApprove(resume)"
            >
              <Loader2 v-if="actionLoading === resume.id" class="w-3 h-3 animate-spin" />
              <CheckCircle2 v-else class="w-3.5 h-3.5" />
              Approve
            </button>

            <!-- Replace file (only for GCS-hosted resumes) -->
            <template v-if="canManage() && resume.file">
              <input
                :ref="el => setReplaceInputRef(resume.id, el as HTMLInputElement)"
                type="file"
                accept="application/pdf"
                class="hidden"
                @change="e => onReplaceFileSelected(resume, e)"
              />
              <button
                :disabled="actionLoading === resume.id || store.uploading"
                title="Replace PDF"
                class="inline-flex items-center justify-center h-8 w-8 rounded-md border border-gray-200 text-gray-400 hover:text-cosmicPurple hover:bg-cosmicPurple/5 hover:border-cosmicPurple/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                @click="triggerReplaceInput(resume.id)"
              >
                <RefreshCw class="w-3.5 h-3.5" />
              </button>
            </template>

            <!-- Delete -->
            <button
              v-if="canManage()"
              :disabled="resume.is_default || actionLoading === resume.id"
              :title="resume.is_default ? 'Cannot delete the default resume' : 'Delete resume'"
              class="inline-flex items-center justify-center h-8 w-8 rounded-md border border-gray-200 text-gray-400 hover:text-red-600 hover:bg-red-50 hover:border-red-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              @click="handleDelete(resume)"
            >
              <Trash2 class="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <!-- Revision notes — full-width strip inside the card -->
        <div
          v-if="resume.approval_status === 'revision' && resume.reviewer_notes"
          class="flex items-start gap-2 px-4 py-2.5 bg-amber-50 border-t border-amber-200 text-xs text-amber-800"
        >
          <TriangleAlert class="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>{{ resume.reviewer_notes }}</span>
        </div>
        </div><!-- end card -->

      </template>
    </div>

    <!-- Error toast fallback -->

    <div v-if="store.error" class="text-xs text-red-600 text-center">{{ store.error }}</div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import {
  Info, UploadCloud, FileText, ExternalLink,
  Star, StarOff, CheckCircle2, Clock, TriangleAlert,
  Trash2, Loader2, RefreshCw,
} from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { useResumeFilesStore, type ResumeFile } from '@/stores/resumeFiles'
import { useAuth } from '@/composables/useAuth'
import { useToast } from '@/components/ui/toast'

const props = defineProps<{
  userId: string | number
  /** When true the current user is viewing their own profile */
  isOwner: boolean
}>()

const { isGuide, isMentor, isAdmin } = useAuth()
const store = useResumeFilesStore()
const { toast } = useToast()

const MAX_RESUMES = 20

const isPrivileged = computed(() => isGuide.value || isMentor.value || isAdmin.value)

/** Can the current user manage (set-default / delete) this resume? */
const canManage = () => props.isOwner || isPrivileged.value

/** Privileged viewers act on the applicant's account; everyone else on their own. */
const targetUserId = computed(() => (isPrivileged.value ? props.userId : undefined))

// ── Upload state ──────────────────────────────────────────────────────────────
const showUpload = ref(false)
const fileInputRef = ref<HTMLInputElement | null>(null)
const isDragging = ref(false)
const pendingFile = ref<File | null>(null)
const pendingName = ref('')
const uploadError = ref('')

const triggerFileInput = () => fileInputRef.value?.click()

const onFileSelected = (e: Event) => {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  if (file) preparePending(file)
  input.value = ''
}

const onDrop = (e: DragEvent) => {
  isDragging.value = false
  const file = e.dataTransfer?.files?.[0]
  if (!file) return
  if (file.type !== 'application/pdf') {
    toast({ title: 'Invalid file', description: 'Only PDF files are accepted.', variant: 'destructive' })
    return
  }
  preparePending(file)
}

const preparePending = (file: File) => {
  if (file.type !== 'application/pdf') {
    toast({ title: 'Invalid file', description: 'Only PDF files are accepted.', variant: 'destructive' })
    return
  }
  if (store.resumeFiles.length >= MAX_RESUMES) {
    toast({ title: 'Limit reached', description: `You can only upload up to ${MAX_RESUMES} resume files.`, variant: 'destructive' })
    return
  }
  const duplicate = store.resumeFiles.some((r) => fileName(r.file) === file.name)
  if (duplicate) {
    toast({
      title: 'Duplicate filename',
      description: `A resume named "${file.name}" already exists. Rename the file or delete the existing one first.`,
      variant: 'destructive',
    })
    return
  }
  pendingFile.value = file
  pendingName.value = ''
  uploadError.value = ''
}

const cancelUpload = () => {
  pendingFile.value = null
  pendingName.value = ''
  uploadError.value = ''
}

const confirmUpload = async () => {
  if (!pendingFile.value) return
  uploadError.value = ''
  // The applicant profile can still be loading when the resume tab is opened
  // directly via ?tab=resume — uploading now would silently attach the file to
  // the privileged viewer instead of the applicant.
  if (isPrivileged.value && !props.userId) {
    uploadError.value = 'Applicant is still loading. Please try again in a moment.'
    return
  }
  try {
    const uploaded = await store.uploadResumeFile(pendingFile.value, pendingName.value, targetUserId.value)
    cancelUpload()
    showUpload.value = false
    const applicant = `${uploaded.user_first_name || ''} ${uploaded.user_last_name || ''}`.trim()
    toast({
      title: 'Uploaded',
      description: props.isOwner
        ? 'Your resume has been uploaded and is pending review.'
        : `Resume uploaded for ${applicant || 'this applicant'} and is pending review.`,
    })
  } catch {
    uploadError.value = store.error || 'Upload failed.'
  }
}

// ── Resume actions ────────────────────────────────────────────────────────────
const actionLoading = ref<number | null>(null)

const withAction = async (id: number, fn: () => Promise<void>, successMsg: string) => {
  actionLoading.value = id
  try {
    await fn()
    toast({ title: 'Done', description: successMsg })
  } catch {
    toast({ title: 'Error', description: store.error || 'Something went wrong.', variant: 'destructive' })
  } finally {
    actionLoading.value = null
  }
}

const handleSetDefault = (r: ResumeFile) =>
  withAction(r.id, () => store.setDefault(r.id), 'Resume set as default.')

const handleUnsetDefault = (r: ResumeFile) =>
  withAction(r.id, () => store.unsetDefault(r.id), 'Default removed.')

const handleApprove = (r: ResumeFile) =>
  withAction(r.id, () => store.approve(r.id), 'Resume approved.')

const handleDelete = async (r: ResumeFile) => {
  if (r.is_default) return
  if (!confirm(`Delete "${r.name || fileName(r.file)}"? This cannot be undone.`)) return
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  withAction(r.id, () => store.deleteResumeFile(r.id), 'Resume deleted.')
}

// ── Replace file ─────────────────────────────────────────────────────────────
const replaceInputRefs: Record<number, HTMLInputElement> = {}

const setReplaceInputRef = (id: number, el: HTMLInputElement | null) => {
  if (el) replaceInputRefs[id] = el
}

const triggerReplaceInput = (id: number) => {
  replaceInputRefs[id]?.click()
}

const onReplaceFileSelected = async (resume: ResumeFile, e: Event) => {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return

  if (file.type !== 'application/pdf') {
    toast({ title: 'Invalid file', description: 'Only PDF files are accepted.', variant: 'destructive' })
    return
  }

  await withAction(resume.id, async () => { await store.replaceFile(resume.id, file) },
    'Resume replaced. It is now pending review.')
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const fileName = (url: string | null | undefined) => url?.split('/').pop() || url || ''

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })

// ── Load ──────────────────────────────────────────────────────────────────────
const loadFiles = () => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  store.fetchResumeFiles(targetUserId.value)
}

onMounted(loadFiles)
watch(() => props.userId, loadFiles)
</script>
